import {useCallback, useEffect, useRef, useState} from 'react'
import {tripService} from '@/services/tripService'
import {importOrReload} from '@/utils/dynamicImport'

/**
 * AN IN-PAGE CALL TO THE DRIVER, over Twilio's Voice SDK.
 *
 * ── ⚠️ WHY THIS EXISTS ALONGSIDE THE PROXY NUMBER ─────────────────────────────────
 * A `tel:` handoff is the better answer on a phone: no permission, no interface, and the
 * device's own dialler is what people expect. It does nothing at all on a desktop browser,
 * where there is no dialler to hand off to — so a passenger on a laptop had a dead button.
 * This is the answer for them, and the cost is everything a real call needs: a microphone,
 * a ringing state, and somewhere to hang up.
 *
 * ── ⚠️ THE TOKEN IS FETCHED PER CALL, NOT KEPT ────────────────────────────────────
 * It carries its own expiry and the SDK does not renew it. A device registered with a
 * stale token fails to connect with an error that explains nothing, which is a far worse
 * failure than one extra request at the moment of pressing call.
 *
 * ── ⚠️ THE DEVICE IS DESTROYED ON THE WAY OUT, ALWAYS ─────────────────────────────
 * It holds a websocket and a microphone stream. A passenger who navigates away mid-call
 * with the device left registered keeps both, and the browser goes on showing the
 * recording indicator on a page that is no longer open.
 *
 * ── ⚠️ AND THE PAGE MUST NOT BE LEFT MID-CALL ─────────────────────────────────────
 * A refresh or a navigation kills it outright. There is no backgrounded-app equivalent on
 * the web and nothing to resume, so the only protection is to ask before it happens.
 */

/**
 * WHAT TO SAY WHEN A CALL CANNOT BE PLACED.
 *
 * ⚠️ MATCHED ON THE KEY FIRST AND THE STATUS ONLY AS A FALLBACK. A key survives a
 * status-code change, and these two mean genuinely different things that a passenger can
 * act on differently: one is "this feature is not switched on yet", the other is "not for
 * this trip, right now".
 */
function callErrorCopy(result) {
    const key = result?.key || result?.raw?.key
    const status = result?.statusCode

    if (key === 'call_token_unavailable' || status === 503) {
        return 'In-app calling is not enabled yet. Please try again later.'
    }
    if (key === 'call_proxy_unavailable' || status === 409) {
        return 'Calling is not available for this trip right now.'
    }
    return result?.error || 'Could not start a call. Please try again.'
}

/** Call lifecycle, in the order it happens. `idle` is also where a finished call lands. */
export const CALL_STATE = {
    idle: 'idle',
    preparing: 'preparing',
    ringing: 'ringing',
    connected: 'connected',
    /**
     * ⚠️ RECONNECTING IS NOT A HANG-UP, and treating it as one is the mistake worth
     * naming. Twilio raises it when the media path drops and it is trying to rebuild — a
     * phone moving between cells, a laptop switching to a different network — and the call
     * usually comes back within seconds. Tearing the screen down on it ends a call that
     * was recovering, and the person is left thinking the other party hung up.
     *
     * It matters more for drivers than passengers: one of them is moving at 60km/h.
     */
    reconnecting: 'reconnecting',
    failed: 'failed',
}

export function useInAppCall(tripId) {
    const [state, setState] = useState(CALL_STATE.idle)
    const [error, setError] = useState('')
    const [muted, setMuted] = useState(false)

    const deviceRef = useRef(null)
    const callRef = useRef(null)

    /* One teardown, used by every exit: hang up, destroy, forget. Written once because
       three code paths need exactly the same thing and a missed one leaks a microphone. */
    const teardown = useCallback(() => {
        try { callRef.current?.disconnect() } catch { /* already gone */ }
        try { deviceRef.current?.destroy() } catch { /* already gone */ }
        callRef.current = null
        deviceRef.current = null
        setMuted(false)
    }, [])

    const hangUp = useCallback(() => {
        teardown()
        setState(CALL_STATE.idle)
    }, [teardown])

    const toggleMute = useCallback(() => {
        const call = callRef.current
        if (!call) return
        const next = !call.isMuted()
        call.mute(next)
        setMuted(next)
    }, [])

    const start = useCallback(async () => {
        if (state !== CALL_STATE.idle && state !== CALL_STATE.failed) return
        setError('')
        setState(CALL_STATE.preparing)

        try {
            /*
             * ⚠️ THE MICROPHONE IS ASKED FOR FIRST, BEFORE THE TOKEN. Two reasons, and the
             * second is the one that matters: a token spent on a call the passenger then
             * refuses to grant a microphone for is a wasted allocation, and the refusal
             * arrives AFTER a confusing pause rather than immediately on the press.
             *
             * ⚠️ AND IT HAS TO BE TRIGGERED BY A REAL GESTURE. Browsers refuse a
             * microphone prompt that is not attached to a user action, so this belongs on
             * the press and nowhere else — never on mount, never on a restored call.
             */
            await navigator.mediaDevices.getUserMedia({audio: true})

            const result = await tripService.getCallToken()
            /* ⚠️ READ THROUGH SEVERAL PATHS. The envelope nests inconsistently across
               releases and the mobile client defends against it rather than assuming one
               shape; a field moving a level deeper should not be a production incident. */
            const token = result?.data?.token
                || result?.data?.call?.token
                || result?.call?.token
                || result?.token

            if (!result?.success || !token) {
                setError(callErrorCopy(result))
                setState(CALL_STATE.failed)
                return
            }

            /* ⚠️ IMPORTED HERE, NOT AT THE TOP OF THE FILE. The SDK is a few hundred
               kilobytes and most passengers never press call; a static import would put it
               in the bundle every one of them downloads. */
            /* ⚠️ VIA `importOrReload`, because this fetch happens long after the page
               loaded — a tab opened before a deploy asks for a chunk name that no longer
               exists and gets a 404 the passenger reads as "Call failed". See there. */
            const {Device} = await importOrReload(() => import('@twilio/voice-sdk'))

            const device = new Device(token, {codecPreferences: ['opus', 'pcmu']})
            deviceRef.current = device

            /* ⚠️ THE ERROR HANDLER GOES ON BEFORE `register()`, not after. Registration is
               the first thing that can fail — a rejected token fails exactly there — and a
               device with no listener yet raises an unhandled error instead of clearing
               the screen. A device-level error is always fatal to the call, so the UI must
               not be left sitting on "connecting" forever. */
            device.on('error', (err) => {
                setError(err?.message || 'Could not start a call. Please try again.')
                teardown()
                setState(CALL_STATE.failed)
            })

            /* A device that has not registered has no signalling connection to place a
               call over, and `connect` on one fails in a way that reads as a backend
               problem rather than a missing step here. */
            await device.register()

            /* ⚠️ `tripId`, CAMEL CASE, AND IT IS THE WHOLE MASKING MECHANISM. Twilio
               forwards these params to the backend's outbound voice webhook, which
               resolves the trip to the driver's line and bridges the two legs. Send the
               wrong key and the call connects to Twilio and rings nobody — a failure with
               no error anywhere. Confirmed against the integration guide, not guessed. */
            const call = await device.connect({params: {tripId: String(tripId)}})
            callRef.current = call
            setState(CALL_STATE.ringing)

            call.on('accept', () => setState(CALL_STATE.connected))
            /* A transient banner, never a teardown — see `CALL_STATE.reconnecting`. */
            call.on('reconnecting', () => setState(CALL_STATE.reconnecting))
            call.on('reconnected', () => setState(CALL_STATE.connected))
            /* Both ends of a hang-up land here: the driver ringing off, and the network
               dropping. Either way the call is over and the device has no further use. */
            call.on('disconnect', () => { teardown(); setState(CALL_STATE.idle) })
            call.on('cancel', () => { teardown(); setState(CALL_STATE.idle) })
            call.on('reject', () => { teardown(); setState(CALL_STATE.idle) })
        } catch (err) {
            /* The most common failure by far is the microphone being refused, and the
               SDK's own message for it is not something to show a passenger. */
            const denied = err?.name === 'NotAllowedError' || /permission/i.test(err?.message || '')
            setError(denied
                ? 'Microphone access is required to call your driver.'
                : err?.message || 'Could not start a call. Please try again.')
            teardown()
            setState(CALL_STATE.failed)
        }
    }, [state, teardown, tripId])

    /* ⚠️ AND ON UNMOUNT, whatever state the call was in. Navigating away is not a reason
       to keep a microphone open. */
    useEffect(() => teardown, [teardown])

    /*
     * ⚠️ ASK BEFORE LEAVING A LIVE CALL. A refresh or a navigation ends it outright — there
     * is no backgrounded-app equivalent on the web and nothing to resume — and someone who
     * loses a call mid-sentence to a stray reload has no way to tell that is what happened.
     *
     * ⚠️ ONLY WHILE A CALL IS LIVE. A page that always warns is a page people learn to
     * dismiss without reading.
     */
    const live = state === CALL_STATE.ringing
        || state === CALL_STATE.connected
        || state === CALL_STATE.reconnecting
    useEffect(() => {
        if (!live) return undefined
        const warn = (e) => { e.preventDefault(); e.returnValue = '' }
        window.addEventListener('beforeunload', warn)
        return () => window.removeEventListener('beforeunload', warn)
    }, [live])

    return {
        state,
        error,
        muted,
        busy: state !== CALL_STATE.idle && state !== CALL_STATE.failed,
        start,
        hangUp,
        toggleMute,
    }
}

export default useInAppCall
